import { type ColumnDef, DerivedValueCell, type FilterFieldDef } from '@olytic/foundation';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import type { ObjectRow } from '../objects/objectsApi';
import {
  aiColumnKey,
  type ApiDerivedValue,
  type DerivedField,
  fetchFields,
  fetchValues,
  isDerivable,
  QUEUED_HINT,
  rerunField,
  toDerivedValue,
} from './derivedApi';

/** An AI column in the filter builder, typed by its answer type. */
export function aiFilterField(field: DerivedField): FilterFieldDef {
  const key = aiColumnKey(field);
  switch (field.output_type) {
    case 'number':
      return { key, label: field.label, kind: 'number' };
    case 'currency':
      return { key, label: field.label, kind: 'currency' };
    case 'date':
      return { key, label: field.label, kind: 'date', relativeDates: true };
    case 'boolean':
      return { key, label: field.label, kind: 'boolean', options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] };
    case 'picklist':
      return { key, label: field.label, kind: 'select', options: field.options.map((o) => ({ value: o, label: o })) };
    default:
      return { key, label: field.label, kind: 'text' };
  }
}

/**
 * A list's AI columns: one per derived field of the object, hidden until
 * someone picks it in the column chooser, with the values for the rows on
 * screen. Objects without history (time entries, documents…) have none.
 */
export function useAiColumns(object: string, rows: readonly ObjectRow[], onNavigate: (href: string) => void) {
  const derivable = isDerivable(object);
  const [fields, setFields] = useState<DerivedField[]>([]);
  const [fieldsVersion, setFieldsVersion] = useState(0);
  const [values, setValues] = useState<Map<string, ApiDerivedValue>>(new Map());
  const [valuesVersion, setValuesVersion] = useState(0);

  useEffect(() => {
    if (!derivable) return;
    const controller = new AbortController();
    fetchFields(authenticatedFetch, object, { signal: controller.signal }).then(
      (result) => { if (!controller.signal.aborted) setFields(result); },
      () => { /* No AI columns when the fields can't load. */ },
    );
    return () => controller.abort();
  }, [derivable, object, fieldsVersion]);

  const ids = rows.map((row) => row.id).join(',');
  useEffect(() => {
    if (!derivable || fields.length === 0 || ids === '') return;
    const controller = new AbortController();
    fetchValues(authenticatedFetch, object, ids.split(','), { signal: controller.signal }).then(
      (result) => {
        if (controller.signal.aborted) return;
        setValues(new Map(result.map((v) => [`${v.fieldId}:${v.recordId}`, v])));
      },
      () => { /* The cells stay empty. */ },
    );
    return () => controller.abort();
  }, [derivable, object, fields.length, ids, valuesVersion]);

  const rerun = useCallback(async (field: DerivedField, recordId: string) => {
    await rerunField(authenticatedFetch, field.id, [recordId]);
    setValuesVersion((n) => n + 1);
  }, []);

  const columns = useMemo<ColumnDef<ObjectRow>[]>(
    () =>
      fields.map((field) => ({
        key: aiColumnKey(field),
        label: field.label,
        ai: true,
        hidden: true,
        // Sorted on the server by the value (apps/server/src/objects.mjs).
        sortable: true,
        get: (row: ObjectRow) => values.get(`${field.id}:${row.id}`)?.value ?? null,
        render: (row: ObjectRow) => {
          const value = values.get(`${field.id}:${row.id}`);
          return value ? (
            <DerivedValueCell
              value={toDerivedValue(field, value)}
              label={field.instruction}
              pendingHint={QUEUED_HINT}
              onNavigate={onNavigate}
              onRerun={() => void rerun(field, row.id)}
            />
          ) : null;
        },
      })),
    [fields, values, onNavigate, rerun],
  );

  const filterFields = useMemo<FilterFieldDef[]>(() => fields.map(aiFilterField), [fields]);

  return {
    derivable,
    filterFields,
    fields,
    columns,
    reloadFields: () => setFieldsVersion((n) => n + 1),
    reloadValues: () => setValuesVersion((n) => n + 1),
  };
}
